import { ReactNode } from "react";
import { ShieldCheck, Sparkles, Globe2 } from "lucide-react";

interface Props {
  title: string;
  subtitle?: string;
  children: ReactNode;
}

/** Two-column shell used by every auth page (login, invite, forgot, reset).
 *  Left: SmartComply hero on the brand gradient. Right: the form card.
 *  Collapses to single-column below md so mobile users get the form first. */
export function AuthLayout({ title, subtitle, children }: Props) {
  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-slate-50">
      {/* Hero — hidden on mobile so the form is above the fold. */}
      <aside
        className="hidden md:flex brand-gradient text-white flex-col justify-between p-10"
        aria-hidden="true"
      >
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-white/95 flex items-center justify-center shadow-sm">
            <img src="/logo.png" alt="" className="h-10 w-10 object-contain" />
          </div>
          <div>
            <div className="text-xl font-semibold tracking-tight leading-tight">SmartComply</div>
            <div className="text-xs text-white/70">by SmartRoots</div>
          </div>
        </div>

        <div className="max-w-md">
          <h2 className="text-3xl font-semibold leading-tight tracking-tight">
            PDFs that everyone can actually read.
          </h2>
          <p className="mt-3 text-white/80 text-sm leading-relaxed">
            SmartComply turns your uploaded PDFs into fully accessible web
            documents — searchable, translatable, and screen-reader friendly by
            default.
          </p>
          <ul className="mt-6 space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />
              <span>WCAG 2.1 AA compliant transcripts, generated automatically.</span>
            </li>
            <li className="flex items-start gap-3">
              <Sparkles className="h-4 w-4 mt-0.5 shrink-0" />
              <span>Ask questions of any document — grounded on the real content.</span>
            </li>
            <li className="flex items-start gap-3">
              <Globe2 className="h-4 w-4 mt-0.5 shrink-0" />
              <span>Translate on the fly into 100+ languages, one click.</span>
            </li>
          </ul>
        </div>

        <div className="text-xs text-white/60">
          © {new Date().getFullYear()} SmartComply · SmartRoots (an OnPoint Insights company)
        </div>
      </aside>

      {/* Form column */}
      <section className="flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">
          {/* Mobile-only logo — the hero is hidden below md so we still brand here. */}
          <div className="md:hidden flex items-center gap-2.5 mb-6 justify-center">
            <div className="h-10 w-10 rounded-lg bg-white shadow-sm flex items-center justify-center border border-brand-100">
              <img src="/logo.png" alt="" className="h-8 w-8 object-contain" />
            </div>
            <div className="text-lg font-semibold text-brand-700">SmartComply</div>
          </div>

          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-ink">{title}</h1>
            {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
          </div>

          {children}
        </div>
      </section>
    </div>
  );
}
